import type { CaptionListenerApi } from './types';

import { sendAnalyticsError } from 'features/analytics';
import type { RootState } from 'features/app/store/store';
import { callIdSelector } from 'features/call/call-base/store';
import { CaptionStatusType } from 'features/call/call-captions/enums';
import { toCaptionStatusTarget } from 'features/call/call-captions/helpers';
import { reportCaptionStatus } from 'features/call/call-captions/services';
import {
  selectCaptionRequests,
  type CaptionRequester,
} from 'features/call/call-captions/store';
import { isAnyHearingConnectedSelector } from 'features/call/call-hearing/store';
import { isPrimaryVcoSenderSelector } from 'features/vco/store';

const captionRequesters: CaptionRequester[] = [
  'localInterpreter',
  'remoteInterpreter',
  'deafEndpoint',
];

export interface CaptionStatusChange {
  requester: CaptionRequester;
  status: CaptionStatusType;
}

// Reference checks match the memoized selectors. A new requests object, a
// hearing-connectivity change, or a VI1 ownership change re-evaluates reports.
export const didCaptionDeliveryInputsChange = (
  currentState: RootState,
  previousState: RootState
) =>
  selectCaptionRequests(currentState) !==
    selectCaptionRequests(previousState) ||
  isAnyHearingConnectedSelector(currentState) !==
    isAnyHearingConnectedSelector(previousState) ||
  isPrimaryVcoSenderSelector(currentState) !==
    isPrimaryVcoSenderSelector(previousState);

// Requested is who wants captions. Active is captions actually being sent.
const isCaptionDeliveryActive = (
  requested: boolean,
  hearingConnected: boolean
) => requested && hearingConnected;

// On when this client starts sending. Off only for an On it already sent.
// Takeover does not inherit the previous owner's Ons, so still-active captions
// are reported On by the new VI1.
const statusForRequester = (
  gainedOwnership: boolean,
  previouslyActive: boolean,
  currentlyActive: boolean
): CaptionStatusType | null => {
  const wasReportedByThisClient = gainedOwnership ? false : previouslyActive;

  if (wasReportedByThisClient === currentlyActive) {
    return null;
  }

  return currentlyActive ? CaptionStatusType.On : CaptionStatusType.Off;
};

export const getCaptionStatusChanges = (
  currentState: RootState,
  previousState: RootState
): CaptionStatusChange[] => {
  if (!isPrimaryVcoSenderSelector(currentState)) {
    return [];
  }

  const gainedOwnership = !isPrimaryVcoSenderSelector(previousState);
  const previousRequests = selectCaptionRequests(previousState);
  const currentRequests = selectCaptionRequests(currentState);
  const wasHearingConnected = isAnyHearingConnectedSelector(previousState);
  const hearingConnected = isAnyHearingConnectedSelector(currentState);
  const changes: CaptionStatusChange[] = [];

  for (const requester of captionRequesters) {
    const status = statusForRequester(
      gainedOwnership,
      isCaptionDeliveryActive(previousRequests[requester], wasHearingConnected),
      isCaptionDeliveryActive(currentRequests[requester], hearingConnected)
    );

    if (!status) {
      continue;
    }

    changes.push({ requester, status });
  }

  return changes;
};

export const reportCaptionStatusChange = async (
  api: CaptionListenerApi,
  { requester, status }: CaptionStatusChange
) => {
  const state = api.getState();
  const target = toCaptionStatusTarget(state, requester);

  try {
    await reportCaptionStatus({
      callId: callIdSelector(state),
      captionStatusType: status,
      requesterType: target.requesterType,
      userId: target.userId,
      dispatch: api.dispatch,
    });
  } catch (error) {
    // Request failures are logged by the reporter. This covers an unexpected
    // throw so one requester cannot skip the rest of the diff.
    api.dispatch(
      sendAnalyticsError({
        Method: 'reportCaptionStatus',
        Message: 'Caption status report failed',
        error,
        requester,
      })
    );
  }
};
